/**
 * @fileoverview Lob Payment Integration Business Logic Helpers
 *
 * This module handles business logic specific to Lob check mailing service integration:
 * - Lob payment validation and requirements checking
 * - Lob payment data preparation for API submission
 * - Lob payment state analysis and job status tracking
 * - Lob-specific UI warnings and field locking logic
 *
 * Business Context:
 * - Lob is a third-party service for automated check printing and mailing
 * - Lob payments have strict validation requirements (character limits, required fields)
 * - Lob payments go through job processing with various status states
 * - Failed/cancelled Lob jobs can be requeued, but completed ones cannot be duplicated
 * - Lob payments require exactly one invoice document and specific address formatting
 *
 * Integration Rules:
 * - Only CHECK payment method is supported for Lob payments
 * - Address fields have combined character limits due to Lob API constraints
 * - Memo and name fields have individual character limits
 * - Existing Lob payments (with lobId) cannot be edited to prevent duplicate checks
 * - Active Lob jobs lock the payment until job completes or fails
 *
 * @author Extracted during PaymentDialog refactoring
 * @since Phase 1 refactoring - business logic extraction
 */

import { transformStringToNumber } from "./numberUtils";
import { PayeeFormInputs } from "./types";

/**
 * Prepare Lob payment data for API submission
 *
 * Transforms payment form data into the format required by the Lob check
 * creation API, mapping form fields to Lob-specific field names.
 *
 * Field Mapping:
 * - recipientName → recipient
 * - companyName → company
 * - addressLine1/2 → addressLine1/2
 * - payeeType preserved for business logic
 * - associations mapped to reportId/facilityId structure
 *
 * @param formData - Payment form data
 * @param invoiceDocumentId - ID of the required invoice document
 * @param tenantId - Organization ID for the payment
 * @returns Formatted data for Lob API submission
 */
export function prepareLobCheckData(
  formData: PayeeFormInputs,
  invoiceDocumentId: string,
  tenantId: string,
  submissionId?: string
) {
  return {
    invoiceDocumentId,
    associations: formData.associations.map((r) => ({
      reportId: r.reportId,
      facilityId: r.facilityId,
      amount: transformStringToNumber(String(r.amount)),
    })),
    activityId: formData.activityId,
    submissionId: submissionId,
    tenantId,
    amount: transformStringToNumber(String(formData.amount)),
    payeeType: formData.payeeType,
    recipient: formData.recipientName,
    invoiceNumber: formData.invoiceNumber,
    memo: formData.memo,
    company: formData.companyName,
    addressLine1: formData.addressLine1,
    addressLine2: formData.addressLine2,
    city: formData.city,
    state: formData.state,
    zip: formData.zip,
  };
}
